import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader } from "@/components/Loader";
import { Plus, Trash2, Save, X, FileCode2, Pencil } from "lucide-react";

const blankField = () => ({ key: "", label: "", type: "text", hint: "" });
const blankPattern = () => ({ name: "", description: "", fields: [blankField()] });

const FIELD_TYPES = [
    { v: "text", l: "Text" },
    { v: "number", l: "Number" },
    { v: "list", l: "List of strings" },
];

export default function Patterns() {
    const [patterns, setPatterns] = useState(null);
    const [editing, setEditing] = useState(null); // pattern object being edited (null = closed)
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/patterns").then(({ data }) => setPatterns(data));
    useEffect(() => { load(); }, []);

    const openNew = () => setEditing(blankPattern());
    const openEdit = (p) => setEditing({ ...p, fields: p.fields.length ? p.fields : [blankField()] });
    const close = () => setEditing(null);

    const setField = (i, k, v) => {
        const next = { ...editing, fields: [...editing.fields] };
        next.fields[i] = { ...next.fields[i], [k]: v };
        setEditing(next);
    };
    const addField = () => setEditing({ ...editing, fields: [...editing.fields, blankField()] });
    const removeField = (i) => setEditing({ ...editing, fields: editing.fields.filter((_, idx) => idx !== i) });

    const save = async () => {
        if (!editing.name.trim()) return;
        setSaving(true);
        try {
            // sanitize keys (snake_case enforced)
            const payload = {
                name: editing.name.trim(),
                description: editing.description || "",
                fields: editing.fields
                    .filter((f) => f.key.trim() && f.label.trim())
                    .map((f) => ({
                        key: f.key.trim().toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, ""),
                        label: f.label.trim(),
                        type: f.type || "text",
                        hint: f.hint || "",
                    })),
            };
            if (editing.id) await api.put(`/patterns/${editing.id}`, payload);
            else await api.post("/patterns", payload);
            await load();
            close();
        } finally {
            setSaving(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete pattern "${p.name}"?`)) return;
        await api.delete(`/patterns/${p.id}`);
        load();
    };

    if (patterns === null) return <div className="p-12"><Loader label="Loading patterns" /></div>;

    return (
        <div className="p-8" data-testid="patterns-page">
            <header className="mb-8 flex items-end justify-between border-b border-zinc-800 pb-6">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Patterns</div>
                    <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Custom research patterns</h1>
                    <p className="text-sm text-zinc-500 mt-2">Define your own fields. The AI fills them on every firm you research with this pattern.</p>
                </div>
                <button onClick={openNew} data-testid="new-pattern-btn" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
                    <Plus className="w-3.5 h-3.5" /> New pattern
                </button>
            </header>

            {patterns.length === 0 ? (
                <div className="border border-dashed border-zinc-800 p-16 text-center">
                    <FileCode2 className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                    <div className="font-heading font-bold text-zinc-300 text-lg mb-2">No patterns yet</div>
                    <div className="text-sm text-zinc-500 mb-5">Create your first pattern — e.g. "ESG audit", "Investor brief", "Tech stack".</div>
                    <button onClick={openNew} className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs uppercase tracking-wider px-4 py-2.5">
                        <Plus className="w-3.5 h-3.5" /> Create pattern
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {patterns.map((p) => (
                        <div key={p.id} data-testid={`pattern-card-${p.id}`} className="border border-zinc-800 bg-zinc-950 p-5 hover:border-amber-500/40 transition-colors">
                            <div className="flex items-start justify-between mb-2">
                                <div className="font-heading font-bold text-lg text-zinc-50">{p.name}</div>
                                <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{p.fields.length} FIELDS</div>
                            </div>
                            <div className="text-xs text-zinc-500 mb-4 min-h-[2.5em] leading-relaxed">{p.description || "No description"}</div>
                            <div className="flex flex-wrap gap-1.5 mb-4">
                                {p.fields.slice(0, 5).map((f) => (
                                    <span key={f.key} className="text-[10px] border border-zinc-700 px-1.5 py-0.5 text-zinc-400 font-mono">{f.key}</span>
                                ))}
                                {p.fields.length > 5 && <span className="text-[10px] text-zinc-500">+{p.fields.length - 5}</span>}
                            </div>
                            <div className="flex gap-2 border-t border-zinc-800 pt-3">
                                <button onClick={() => openEdit(p)} data-testid={`edit-pattern-${p.id}`} className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-amber-500 inline-flex items-center gap-1">
                                    <Pencil className="w-3 h-3" /> Edit
                                </button>
                                <button onClick={() => remove(p)} data-testid={`delete-pattern-${p.id}`} className="text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-red-400 inline-flex items-center gap-1 ml-auto">
                                    <Trash2 className="w-3 h-3" /> Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {editing && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in" data-testid="pattern-modal">
                    <div className="bg-zinc-950 border border-zinc-800 w-full max-w-3xl max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between border-b border-zinc-800 p-4">
                            <div className="font-heading font-bold text-zinc-50">{editing.id ? "Edit pattern" : "New pattern"}</div>
                            <button onClick={close} className="text-zinc-500 hover:text-zinc-100" data-testid="close-pattern-modal"><X className="w-4 h-4" /></button>
                        </div>
                        <div className="p-5 space-y-4 overflow-y-auto">
                            <div>
                                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Pattern name</label>
                                <input
                                    autoFocus
                                    value={editing.name}
                                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                    placeholder="e.g. ESG audit"
                                    data-testid="pattern-name-input"
                                    className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-500"
                                />
                            </div>
                            <div>
                                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Description</label>
                                <input
                                    value={editing.description}
                                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                                    placeholder="What this pattern captures"
                                    data-testid="pattern-description-input"
                                    className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-amber-500"
                                />
                            </div>

                            <div className="border-t border-zinc-800 pt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Fields</div>
                                    <button onClick={addField} data-testid="add-field-btn" className="text-xs font-mono uppercase tracking-widest text-amber-500 hover:text-amber-400 inline-flex items-center gap-1">
                                        <Plus className="w-3 h-3" /> Add field
                                    </button>
                                </div>
                                <div className="space-y-3">
                                    {editing.fields.map((f, i) => (
                                        <div key={i} className="border border-zinc-800 p-3 bg-black/40 grid grid-cols-1 md:grid-cols-12 gap-2" data-testid={`field-row-${i}`}>
                                            <input
                                                value={f.label}
                                                onChange={(e) => setField(i, "label", e.target.value)}
                                                placeholder="Field label (e.g. ESG Score)"
                                                className="md:col-span-3 bg-zinc-950 border border-zinc-800 px-2 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-amber-500"
                                                data-testid={`field-label-${i}`}
                                            />
                                            <input
                                                value={f.key}
                                                onChange={(e) => setField(i, "key", e.target.value)}
                                                placeholder="key (snake_case)"
                                                className="md:col-span-3 bg-zinc-950 border border-zinc-800 px-2 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-amber-500 font-mono"
                                                data-testid={`field-key-${i}`}
                                            />
                                            <select
                                                value={f.type}
                                                onChange={(e) => setField(i, "type", e.target.value)}
                                                className="md:col-span-2 bg-zinc-950 border border-zinc-800 px-2 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-amber-500"
                                                data-testid={`field-type-${i}`}
                                            >
                                                {FIELD_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                                            </select>
                                            <input
                                                value={f.hint}
                                                onChange={(e) => setField(i, "hint", e.target.value)}
                                                placeholder="AI hint (optional, e.g. 0-100)"
                                                className="md:col-span-3 bg-zinc-950 border border-zinc-800 px-2 py-1.5 text-xs text-zinc-100 focus:outline-none focus:border-amber-500"
                                                data-testid={`field-hint-${i}`}
                                            />
                                            <button onClick={() => removeField(i)} className="md:col-span-1 text-zinc-500 hover:text-red-400 inline-flex items-center justify-center" data-testid={`remove-field-${i}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 border-t border-zinc-800 p-4">
                            <button onClick={close} className="border border-zinc-800 hover:border-zinc-600 text-xs font-mono uppercase tracking-widest text-zinc-400 px-4 py-2">Cancel</button>
                            <button onClick={save} disabled={saving || !editing.name.trim()} data-testid="save-pattern-btn" className="bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-semibold text-xs uppercase tracking-wider px-4 py-2 inline-flex items-center gap-2">
                                <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save pattern"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
